import {
  DEFAULT_INDOOR_TEMP,
  HeatPumpAuxilaryEffectRatio,
  HEAT_PUMP_EFFECT_RATIO,
  IHeatingEnergyCalculationOptions,
  DEFAULT_LIMIT_TEMP,
} from './energy.models';
import { getDegreeHoursFromCoordinate } from './temperature.helpers';
import { clamp } from '../math.helpers';
import {
  getDesignOutdoorTemp,
  getNormalYearTemp,
} from './city-climate.helpers';
import { COORDINATE_STOCKHOLM } from '../../constants';
import { HOURS_PER_YEAR } from '../../constants/math.constants';

/**
 * Options used for calculating the electric effect of a heat pump.
 */
type ICalculateElectricEffectOptions = IHeatingEnergyCalculationOptions & {
  /**
   * The Coefficient of Performance (COP) of the heat pump.
   * 3 means that for every 1 kW of electricity, the heat pump can produce 3 kW of heat.
   * 1 means that the heat pump is using 100% direct electric heating.
   */
  cop?: number; // Coefficient of Performance
};

/**
 * Calculate the electric effect (in kW) required to heat a building with a given yearly heat consumption.
 * Pass cop = 1 to get the heating effect.
 * @param annualHeatRequirement - The annual heat requirement in kWh/year
 * @param options - The options for the calculation
 * @returns The electric effect in kW
 */
export const calculateElectricEffect = (
  annualHeatRequirement: number,
  options: ICalculateElectricEffectOptions = {},
): number => {
  const { cop = 1 } = options;

  // How many hours per year should heating be active based on the dimensioning outdoor temperature
  const hours = getHeatingHours(options);

  // the heating effect (in kW) that a building needs to handle outdoor temperature.
  const thermalEffect = annualHeatRequirement / hours;

  // Divide with Coefficient of Performance (COP), basically effiency to get the electric effect
  // A heat pump with a COP of 3.5 will produce 3.5 kW of thermalEffect for every 1 kW of electric effect.
  return thermalEffect / cop;
};

/**
 * Get how many hours per year heating is needed based on the dimensioning outdoor temperature and location.
 * Basically how many hours per year the temperature is below the dimensioning outdoor temperature.
 * @param location Where the building is located
 * @param options The options for the calculation
 * @returns The heating hours
 */
export const getHeatingHours = ({
  location = COORDINATE_STOCKHOLM,
  outdoorTemp = DEFAULT_LIMIT_TEMP,
  indoorTemp = DEFAULT_INDOOR_TEMP,
  degreeHours: overrideDegreeHours,
}: IHeatingEnergyCalculationOptions = {}): number => {
  // Temperature difference between indoor and dimensioning outdoor temperature
  const deltaT = indoorTemp - outdoorTemp;

  if (deltaT <= 0) {
    return 0;
  }

  // WARNING: VOLKSWAGEN FIX. Seems like the degree hours data is incorrect for higher temperatures...
  if (
    outdoorTemp >= DEFAULT_LIMIT_TEMP &&
    deltaT !== 1 && // Don't do this when we try to get 1 heating hour
    overrideDegreeHours !== 1
  ) {
    const normalYearTemp = getNormalYearTemp(location);

    return (
      getHeatingHours({
        location,
        outdoorTemp:
          normalYearTemp === DEFAULT_LIMIT_TEMP
            ? normalYearTemp - 1 // Prevent infinite loop
            : normalYearTemp,
        indoorTemp,
        degreeHours: overrideDegreeHours,
      }) * 2
    );
  }

  // "hours x tempDiff" based on coordinates (currently uses a lot of template values...)
  const degreeHours =
    overrideDegreeHours ?? getDegreeHoursFromCoordinate(location, outdoorTemp);

  return degreeHours / deltaT;
};

/**
 * Get a ratio on how much of the year is heated. 1 means 100% of the year is heated. 0 means 0% of the year is heated.
 * @param options - The options for the calculation
 * @returns The ratio of the heating hours to the total hours in a year
 */
export const getHeatingOperationRatio = (
  options?: IHeatingEnergyCalculationOptions,
) => getHeatingHours(options) / HOURS_PER_YEAR;

/**
 * Calculate the max capacity (in kW) for a heat pump.
 * Max capacity is the maximum heat production capacity required of a heat pump during the coldest design day of the year.
 * @param annualHeatRequirement - The annual heat requirement in kWh/year
 * @param options - The options for the calculation
 * @returns The max heat production capacity in kW
 */
export const calculateMaxHeatProductionCapacity = (
  annualHeatRequirement: number,
  {
    location = COORDINATE_STOCKHOLM,
    indoorTemp = DEFAULT_INDOOR_TEMP,
  }: Omit<ICalculateElectricEffectOptions, 'outdoorTemp' | 'cop'> = {},
): number =>
  calculateElectricEffect(
    annualHeatRequirement * (1 - HEAT_PUMP_EFFECT_RATIO.energy),
    {
      location,
      outdoorTemp: getDesignOutdoorTemp(location),
      indoorTemp,
      cop: 1, // passing 1 will make sure we calculate the heat effect without any effectiveness which will give us the heat capacity
    },
  );

/**
 * Calculate the total energy consumption of a heating system.
 * @param annualHeatRequirement - The annual heat requirement in kWh/year
 * @param maxEffect - The maximum effect (kW) of the heat pump (excluding auxiliary heating)
 * @param auxiliaryEffect - The auxiliary effect (kW) of the heating system (excluding max effect)
 * @param cop - The coefficient of performance of the heating system
 */
export const getHeatPumpEnergyConsumption = (
  annualHeatRequirement: number,
  heatPumpEffectPercentage: number,
  cop: number,
): { heatPumpEnergy: number; auxiliaryEnergy: number } => {
  const auxEnergyPercentage = calculateAuxilaryEnergyCorrelation(
    1 - heatPumpEffectPercentage,
  );
  const auxEnergy = annualHeatRequirement * auxEnergyPercentage;

  return {
    heatPumpEnergy: (annualHeatRequirement - auxEnergy) / cop,
    auxiliaryEnergy: auxEnergy,
  };
};

/**
 * They say that if you cover 70% of the peak effect with the heat pump that is 95% of the energy consumption.
 * @param auxiliaryEffectPercentage - The percentage of the auxiliary effect to the total effect
 * @param effectRatio - The ratio of the heat pump effect to the auxiliary heating effect
 * @returns The percentage of the auxiliary energy to the total energy consumption
 */
export const calculateAuxilaryEnergyCorrelation = (
  auxiliaryEffectPercentage: number,
  { effect, energy }: HeatPumpAuxilaryEffectRatio = HEAT_PUMP_EFFECT_RATIO,
): number => {
  const percent = clamp(auxiliaryEffectPercentage, 0, 1);
  const auxEffectLimit = 1 - effect;
  const auxEnergyLimit = 1 - energy;
  if (percent <= 0) return 0;
  if (percent <= auxEffectLimit) {
    // Linear interpolation between (0,0) and (0.3, 0.05)
    return (auxEnergyLimit / auxEffectLimit) * percent;
  }
  // Exponential curve between (0.3, 0.05) and (1, 1)
  return (
    auxEnergyLimit + energy * Math.pow((percent - auxEffectLimit) / effect, 2)
  );
};
